import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Switch } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import FieldHelp from "components/Ui/FieldHelp/FieldHelp";
import CitySettingsModal from "components/Cities/CitySettingsModal/CitySettingsModal";
import useCity from "hooks/useCity";
import { useWhatsappInstances } from "hooks/useMessagingHealth";
import { getVendorsDropdown } from "services/vendors";
import { useDriverShiftWeek, useUpdateCityDriverShifts } from "hooks/useDriverShifts";
import { palestineToday, WEEKDAYS, WEEKDAY_LABELS } from "utils/driverShiftWeek";
import TimeField from "components/Ui/TimeField/TimeField";

/**
 * The booking settings of a city (api#198), as the form holds them.
 *
 * @param {object|undefined} region
 */
const bookingFormFrom = (region) => ({
    weekStartDay: region?.shift_week_start_day ?? "saturday",
    bookingOpensDay: region?.shift_booking_opens_day ?? "thursday",
    bookingOpensTime: String(region?.shift_booking_opens_time ?? "12:00").slice(0, 5),
    bookingClosesDay: region?.shift_booking_closes_day ?? "friday",
    bookingClosesTime: String(region?.shift_booking_closes_time ?? "18:00").slice(0, 5),
    maxShiftsPerDriver: String(region?.shift_max_per_driver ?? 6),
});

/** Edit the six city fields that change API and message behaviour. */
const CityOperationalSettings = (props) => {
    const { instances: whatsappInstances } = useWhatsappInstances();
    const { loading, editCity, error, fetchCities } = useCity();
    const [driverClaimFromPreparing, setDriverClaimFromPreparing] = useState(false);
    const [whatsappInstance, setWhatsappInstance] = useState(2);
    const [skipVendorOrderWhatsapp, setSkipVendorOrderWhatsapp] = useState(false);
    const [skipUserWhatsapp, setSkipUserWhatsapp] = useState(false);
    const [includeCancellationNotice, setIncludeCancellationNotice] = useState(false);
    const [defaultServiceVendorId, setDefaultServiceVendorId] = useState("");
    // The hours of «طلب خاص» ("HH:mm"); both empty is all day.
    const [customOrdersFrom, setCustomOrdersFrom] = useState("");
    const [customOrdersTo, setCustomOrdersTo] = useState("");
    const [shiftsEnabled, setShiftsEnabled] = useState(false);
    const [shiftOverflowMinutes, setShiftOverflowMinutes] = useState("5");
    // Booking (phase 2): the week, the window, and the limit per driver.
    const [shiftBooking, setShiftBooking] = useState({});
    const setBooking = (field) => (event) =>
        setShiftBooking({ ...shiftBooking, [field]: event.target.value });
    const updateShiftSettings = useUpdateCityDriverShifts();

    // Driver shifts (admin#74). Turning them on in a city with no shift in the
    // next 7 days makes every driver a reserve driver, who gets an order only
    // after the overflow minutes. So the week is read BEFORE the switch is
    // saved, and the form warns.
    const savedShiftsEnabled =
        props.region?.shifts_enabled === true || props.region?.shifts_enabled === 1;
    const turningShiftsOn = shiftsEnabled && !savedShiftsEnabled;
    const { week: shiftWeek } = useDriverShiftWeek(
        turningShiftsOn ? props.region?.id : "",
        palestineToday(),
    );
    const upcomingShiftDrivers = shiftWeek
        ? shiftWeek.templates.reduce(
              (sum, template) => sum + template.cells.reduce((cells, cell) => cells + cell.used, 0),
              0,
          )
        : null;

    const { data: serviceVendors } = useQuery({
        queryKey: ["service-vendors-dropdown"],
        queryFn: () => getVendorsDropdown(),
        staleTime: 5 * 60 * 1000,
    });
    const serviceVendorOptions = (serviceVendors?.results || []).filter(
        (vendor) => vendor.isServiceVendor,
    );

    useEffect(() => {
        setDriverClaimFromPreparing(!!props.region?.driver_claim_from_preparing_enabled);
        setWhatsappInstance(props.region?.whatsapp_instance ?? 2);
        setSkipVendorOrderWhatsapp(!!props.region?.skip_vendor_order_created_whatsapp);
        setSkipUserWhatsapp(!!props.region?.skip_user_whatsapp);
        setIncludeCancellationNotice(
            !!props.region?.include_user_order_whatsapp_cancellation_notice,
        );
        setDefaultServiceVendorId(props.region?.default_service_vendor_id ?? "");
        setCustomOrdersFrom(String(props.region?.custom_orders_from ?? "").slice(0, 5));
        setCustomOrdersTo(String(props.region?.custom_orders_to ?? "").slice(0, 5));
        setShiftsEnabled(
            props.region?.shifts_enabled === true || props.region?.shifts_enabled === 1,
        );
        setShiftOverflowMinutes(String(props.region?.shift_overflow_minutes ?? 5));
        setShiftBooking(bookingFormFrom(props.region));
    }, [props.region]);

    const submit = async (event) => {
        event.preventDefault();
        const regionData = new FormData();

        regionData.append("id", props.region.id);
        regionData.append("driver_claim_from_preparing_enabled", driverClaimFromPreparing);
        regionData.append("whatsapp_instance", whatsappInstance || 2);
        regionData.append("skip_vendor_order_created_whatsapp", skipVendorOrderWhatsapp);
        regionData.append("skip_user_whatsapp", skipUserWhatsapp);
        regionData.append(
            "include_user_order_whatsapp_cancellation_notice",
            includeCancellationNotice,
        );
        // An empty value means that طلب خاص is off in this city.
        regionData.append("default_service_vendor_id", defaultServiceVendorId);
        // Both, or both empty. The API refuses one alone with a sentence.
        regionData.append("custom_orders_from", customOrdersFrom || "");
        regionData.append("custom_orders_to", customOrdersTo || "");

        const overflowMinutes = Number(shiftOverflowMinutes);
        if (!Number.isInteger(overflowMinutes) || overflowMinutes < 0 || overflowMinutes > 120) {
            props.showMessage("error", "تعذر الحفظ", "دقائق الانتظار يجب أن تكون من 0 إلى 120");
            return;
        }

        try {
            const response = await editCity(regionData);
            if (!response) {
                props.showMessage("error", "هناك خطأ", error || "حدث خطأ غير متوقع");
                return;
            }

            // The shift settings have their own endpoint: the generic city
            // PATCH strips them (api#198).
            const savedBooking = bookingFormFrom(props.region);
            const shiftsChanged =
                shiftsEnabled !== savedShiftsEnabled ||
                overflowMinutes !== Number(props.region?.shift_overflow_minutes ?? 5) ||
                Object.keys(savedBooking).some((key) => savedBooking[key] !== shiftBooking[key]);
            if (shiftsChanged) {
                await updateShiftSettings.mutateAsync({
                    cityId: props.region.id,
                    enabled: shiftsEnabled,
                    overflowMinutes,
                    ...shiftBooking,
                    maxShiftsPerDriver: Number(shiftBooking.maxShiftsPerDriver),
                });
                await fetchCities();
            }

            props.showMessage("success", "تم الحفظ", "تم تحديث الإعدادات التشغيلية");
            props.close();
        } catch (err) {
            props.showMessage(
                "error",
                "تعذر الحفظ",
                err.response?.data?.message || error || "حدث خطأ غير متوقع",
            );
        }
    };

    return (
        <CitySettingsModal
            show={props.show}
            close={props.close}
            region={props.region}
            title="إعدادات تشغيلية"
            loading={loading}
            onSubmit={submit}
        >
            <div className="city-settings-modal__section">
                <div className="city-settings-modal__section-title">
                    إعدادات تشغيلية
                    <FieldHelp label="عن الإعدادات التشغيلية">
                        تتحكم بسلوك التطبيق لهذه المدينة. التغيير يسري خلال دقيقة تقريباً.
                    </FieldHelp>
                </div>

                <Form.Group className="mb-3" controlId="driverClaimFromPreparing">
                    <div className="city-settings-modal__toggle">
                        <Form.Label>استلام الطلب من مرحلة التحضير</Form.Label>
                        <FieldHelp label="عن استلام الطلب من مرحلة التحضير">
                            السائق يرى الطلب ويستلمه وهو قيد التحضير، بدل انتظار جهوزيته.
                            <br />
                            <strong>
                                ⚠️ تطبيق السائق يدعمها حالياً في كفر عقب والرام فقط — راجع مطوّر
                                التطبيق قبل تفعيلها لمدينة جديدة، وإلا ستصل السائقين تنبيهات لطلبات
                                لا تظهر في قائمتهم.
                            </strong>
                        </FieldHelp>
                        <Switch
                            color="warning"
                            size="small"
                            checked={driverClaimFromPreparing}
                            onChange={(event) => setDriverClaimFromPreparing(event.target.checked)}
                        />
                    </div>
                </Form.Group>

                <Form.Group className="mb-3" controlId="whatsappInstance">
                    <Form.Label>
                        رقم واتساب المدينة
                        <FieldHelp label="عن رقم واتساب المدينة">
                            الرقم الذي تُرسل منه رسائل هذه المدينة. الرقمان منفصلان ولكل منهما حصة
                            إرسال مستقلة.
                        </FieldHelp>
                    </Form.Label>
                    <Form.Select
                        value={whatsappInstance}
                        onChange={(event) => setWhatsappInstance(+event.target.value)}
                    >
                        {(whatsappInstances || []).map((instance) => (
                            <option key={instance.value} value={instance.value}>
                                {instance.phone
                                    ? `${instance.label} ⁦(${instance.phone})⁩`
                                    : instance.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="skipVendorOrderWhatsapp">
                    <div className="city-settings-modal__toggle">
                        <Form.Label>إيقاف رسالة الطلب الجديد للمطعم</Form.Label>
                        <FieldHelp label="عن إيقاف رسالة الطلب الجديد للمطعم">
                            لا تصل المطعم رسالة واتساب عند وصول طلب جديد. يبقى الإشعار داخل التطبيق
                            كما هو.
                        </FieldHelp>
                        <Switch
                            color="warning"
                            size="small"
                            checked={skipVendorOrderWhatsapp}
                            onChange={(event) => setSkipVendorOrderWhatsapp(event.target.checked)}
                        />
                    </div>
                </Form.Group>

                <Form.Group className="mb-3" controlId="skipUserWhatsapp">
                    <div className="city-settings-modal__toggle">
                        <Form.Label>إيقاف رسائل الطلب للزبون</Form.Label>
                        <FieldHelp label="عن إيقاف رسائل الطلب للزبون">
                            توقف فاتورة الطلب وتحديثات الحالة.
                            <br />
                            رسائل رمز التحقق (OTP) تُرسل دائماً ولا يوقفها هذا الخيار.
                        </FieldHelp>
                        <Switch
                            color="warning"
                            size="small"
                            checked={skipUserWhatsapp}
                            onChange={(event) => setSkipUserWhatsapp(event.target.checked)}
                        />
                    </div>
                </Form.Group>

                <Form.Group className="mb-3" controlId="includeCancellationNotice">
                    <div className="city-settings-modal__toggle">
                        <Form.Label>تنبيه إلغاء الطلب في رسالة الزبون</Form.Label>
                        <FieldHelp label="عن تنبيه إلغاء الطلب">
                            يضيف سطراً لفاتورة الزبون: يمكنه الإلغاء خلال 3 دقائق مع رقم الدعم.
                            فعّلها فقط حيث يوجد من يرد على الاتصال.
                        </FieldHelp>
                        <Switch
                            color="warning"
                            size="small"
                            checked={includeCancellationNotice}
                            onChange={(event) => setIncludeCancellationNotice(event.target.checked)}
                        />
                    </div>
                </Form.Group>

                <Form.Group className="mb-3" controlId="defaultServiceVendor">
                    <Form.Label>
                        متجر الطلب الخاص
                        <FieldHelp label="عن متجر الطلب الخاص">
                            الحساب الذي تُسجَّل عليه طلبات الزبائن الخاصة في هذه المدينة. اتركه
                            فارغاً لإيقاف الخدمة هنا — وهذا هو الوضع الطبيعي لمدينة بلا مكتب توصيل.
                            تظهر هنا الحسابات المعلَّمة كـ&quot;متجر خدمات&quot; فقط، ويجوز اختيار
                            حساب من مدينة أخرى.
                        </FieldHelp>
                    </Form.Label>
                    <Form.Select
                        value={defaultServiceVendorId}
                        onChange={(event) => setDefaultServiceVendorId(event.target.value)}
                    >
                        <option value="">لا يوجد — الخدمة موقوفة</option>
                        {serviceVendorOptions.map((vendor) => (
                            <option key={vendor.vendorId} value={vendor.vendorId}>
                                {vendor.name}
                                {vendor.city ? ` — ${vendor.city}` : ""}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="customOrdersFrom">
                    <Form.Label>
                        ساعات الطلب الخاص
                        <FieldHelp label="عن ساعات الطلب الخاص">
                            يظهر زر «طلب خاص» للزبائن في هذه الساعات فقط، بتوقيت فلسطين. يمكن أن تمر
                            الساعات بمنتصف الليل، مثل 11:00 مساءً إلى 1:00 صباحاً. اترك الوقتين
                            فارغين ليبقى الطلب الخاص متاحاً طول اليوم.
                        </FieldHelp>
                    </Form.Label>
                    <div className="d-flex gap-2 align-items-center">
                        <span>من</span>
                        <TimeField
                            inputId="customOrdersFrom"
                            className="w-100"
                            value={customOrdersFrom}
                            onChange={(value) => setCustomOrdersFrom(value || "")}
                        />
                        <span>إلى</span>
                        <TimeField
                            inputId="customOrdersTo"
                            className="w-100"
                            value={customOrdersTo}
                            onChange={(value) => setCustomOrdersTo(value || "")}
                        />
                    </div>
                </Form.Group>
            </div>

            <div className="city-settings-modal__section">
                <div className="city-settings-modal__section-title">
                    ورديات السائقين
                    <FieldHelp label="عن ورديات السائقين">
                        عند التفعيل يصل تنبيه الطلب الجديد فقط للسائقين الذين في ورديتهم ومتصلون،
                        وهم فقط يستطيعون استلامه. الورديات تُضبط من صفحة «ورديات السائقين».
                    </FieldHelp>
                </div>

                <Form.Group className="mb-3" controlId="shiftsEnabled">
                    <div className="city-settings-modal__toggle">
                        <Form.Label>تفعيل الورديات</Form.Label>
                        <Switch
                            color="warning"
                            size="small"
                            checked={shiftsEnabled}
                            onChange={(event) => setShiftsEnabled(event.target.checked)}
                        />
                    </div>
                </Form.Group>
                {turningShiftsOn && upcomingShiftDrivers === 0 ? (
                    <div className="form-warning">
                        لا يوجد أي سائق في وردية خلال الأيام السبعة القادمة. بعد التفعيل يصبح كل
                        السائقين احتياطًا، ولا يصلهم الطلب إلا بعد دقائق الانتظار. أضف الورديات
                        أولًا.
                    </div>
                ) : null}

                <Form.Group className="mb-3" controlId="shiftOverflowMinutes">
                    <Form.Label>
                        دقائق الانتظار قبل السائقين الاحتياط
                        <FieldHelp label="عن دقائق الانتظار">
                            إذا لم يستلم الطلبَ أي سائق في ورديته خلال هذه الدقائق، يصل التنبيه
                            للسائقين خارج ورديتهم وهم متصلون، ويستطيعون استلامه. 0 يعني أبدًا.
                        </FieldHelp>
                    </Form.Label>
                    <Form.Control
                        type="number"
                        min={0}
                        max={120}
                        inputMode="numeric"
                        value={shiftOverflowMinutes}
                        onChange={(event) => setShiftOverflowMinutes(event.target.value)}
                    />
                </Form.Group>

                <div className="city-settings-modal__section-title">
                    حجز السائقين
                    <FieldHelp label="عن حجز السائقين">
                        يحجز السائق ورديات الأسبوع القادم كاملًا بنفسه، أول من يحجز يأخذ المكان. بعد
                        إغلاق الحجز يغيّر الورديات المشرف فقط.
                    </FieldHelp>
                </div>
                <div className="city-shift-booking">
                    <Form.Group controlId="shiftWeekStartDay">
                        <Form.Label>بداية الأسبوع</Form.Label>
                        <Form.Select
                            value={shiftBooking.weekStartDay}
                            onChange={setBooking("weekStartDay")}
                        >
                            {WEEKDAYS.map((day) => (
                                <option key={day} value={day}>
                                    {WEEKDAY_LABELS[day]}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group controlId="shiftMaxPerDriver">
                        <Form.Label>ورديات لكل سائق في الأسبوع</Form.Label>
                        <Form.Control
                            type="number"
                            min={0}
                            max={50}
                            inputMode="numeric"
                            value={shiftBooking.maxShiftsPerDriver}
                            onChange={setBooking("maxShiftsPerDriver")}
                        />
                    </Form.Group>
                    <Form.Group controlId="shiftBookingOpensDay">
                        <Form.Label>يفتح الحجز</Form.Label>
                        <Form.Select
                            value={shiftBooking.bookingOpensDay}
                            onChange={setBooking("bookingOpensDay")}
                        >
                            {WEEKDAYS.map((day) => (
                                <option key={day} value={day}>
                                    {WEEKDAY_LABELS[day]}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group controlId="shiftBookingOpensTime">
                        <Form.Label>الساعة</Form.Label>
                        <TimeField
                            inputId="shiftBookingOpensTime"
                            className="w-100"
                            value={shiftBooking.bookingOpensTime}
                            onChange={(value) =>
                                setShiftBooking({ ...shiftBooking, bookingOpensTime: value })
                            }
                        />
                    </Form.Group>
                    <Form.Group controlId="shiftBookingClosesDay">
                        <Form.Label>يغلق الحجز</Form.Label>
                        <Form.Select
                            value={shiftBooking.bookingClosesDay}
                            onChange={setBooking("bookingClosesDay")}
                        >
                            {WEEKDAYS.map((day) => (
                                <option key={day} value={day}>
                                    {WEEKDAY_LABELS[day]}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group controlId="shiftBookingClosesTime">
                        <Form.Label>الساعة</Form.Label>
                        <TimeField
                            inputId="shiftBookingClosesTime"
                            className="w-100"
                            value={shiftBooking.bookingClosesTime}
                            onChange={(value) =>
                                setShiftBooking({ ...shiftBooking, bookingClosesTime: value })
                            }
                        />
                    </Form.Group>
                </div>
                <div className="city-shift-booking__note">
                    0 ورديات يعني بلا حد. يجب أن يفتح الحجز قبل أن يغلق، وكلاهما قبل بداية الأسبوع.
                </div>
            </div>
        </CitySettingsModal>
    );
};

export default CityOperationalSettings;
